// Pure-JSON, serializable sidebar schema. NO functions, NO component references.
// The gateway's sidebar engine (apps/gateway/src/gateway/sidebar/engine/)
// interprets these descriptors against the Clarix stack (react-router,
// @tanstack/react-query + @clarix/api-client, @clarix/rbac, localStorage).
//
// Golden rule: a config never ships rendering. If a look or behaviour isn't in
// this vocabulary yet, add it to the engine ONCE (a new field / variant) and
// then every app selects it from JSON — that is what keeps all sidebars
// identical. Everything declared here is implemented by the engine; nothing in
// this file is aspirational.

import type { PermissionKey } from './rbac';

/** Current schema version. Bump when the vocabulary changes incompatibly. */
export const SIDEBAR_SCHEMA_VERSION = 1;

/* ─────────────────────────── Vocabulary primitives ─────────────────────────── */

/** Icon by NAME — resolved via the engine's bounded icon registry. */
export type IconName = string;

/** A declarative API request executed via TanStack Query + @clarix/api-client. */
export interface ApiRequest {
  /** API_ENDPOINTS value or absolute path. */
  endpoint: string;
  /** `base` (default) returns the raw envelope; `api` is pre-unwrapped. */
  service?: 'base' | 'api';
  /** Static params, or `{token}` templates resolved from the ctx bag. */
  params?: Record<string, string | number | boolean>;
  staleTime?: number;
}

/**
 * Declarative shaping of an API response into rows (the pure-data replacement
 * for a `map` callback).
 *  - `listPath` dot-path to the array in the response (e.g. "data").
 *  - `id` / `label` / `badge` dot-paths into each record.
 *  - `to` an href template with `{field}` interpolation from the record.
 */
export interface RowMapping {
  listPath?: string;
  id: string;
  label: string;
  /** Fallback dot-path when `label` is empty/null (e.g. title → description). */
  labelFallback?: string;
  icon?: IconName;
  /** Dot-path rendered as the trailing muted code (e.g. a spark code). */
  badge?: string;
  to?: string;
  /** Leading dot whose colour is a raw value (hex) read from the record. */
  colorDotField?: string;
}

/** Value-count read for a single number (e.g. pagination.total). */
export interface CountRequest {
  api: ApiRequest;
  /** dot-path to a numeric value in the response (e.g. "pagination.total"). */
  valuePath?: string;
  /**
   * Count the length of the array at this dot-path instead of reading a number.
   * Empty string = the response payload is itself the array (e.g. monitoring).
   */
  lengthPath?: string;
}

/**
 * The bounded tone palette. One home for every sidebar colour so a panel can
 * never invent one — the engine maps a tone to a static (JIT-safe) class.
 */
export type BadgeTone =
  | 'gray'
  | 'slate'
  | 'indigo'
  | 'emerald'
  | 'teal'
  | 'amber'
  | 'red'
  | 'green'
  | 'orange';

/**
 * `count` = a toned pill (a number that wants attention).
 * `traffic-light` = a pill whose tone comes from thresholds.
 * `plain` = a muted number with no pill — for a value that is merely the SIZE
 * of a list (how many departments) rather than something to act on.
 */
export type BadgeKind = 'count' | 'traffic-light' | 'plain';

/** A badge shown on a row (right-aligned). */
export interface BadgeConfig {
  kind: BadgeKind;
  /** A single count request… */
  count?: CountRequest;
  /** …or the sum of several (e.g. "All users" = active + deactivated + invited). */
  sum?: CountRequest[];
  /** …or a value a provider already resolved. */
  value?: number;
  /** For traffic-light: [warnAtOrAbove, dangerAtOrAbove]. Default [1, 10]. */
  thresholds?: [number, number];
  tone?: BadgeTone;
}

/* ─────────────────────────── Predicates / visibility ─────────────────────────── */

export interface Predicate {
  permission?: PermissionKey | PermissionKey[];
  /** When `permission` is an array, require ANY rather than ALL. */
  permissionAny?: boolean;
  /**
   * Resolve `permission` against ORG-level base permissions only, ignoring
   * project-scoped elevation. Global chrome (rail, panels) uses this so a
   * per-project grant can't light up an app-wide control.
   */
  base?: boolean;
  /**
   * Resolve `permission` per project: visible only when some project the user can
   * access grants it. For entry points to project-scoped data that is otherwise empty.
   */
  anyProject?: boolean;
  /** Per-project permission — evaluated with the row's id as the project id. */
  projectPermission?: PermissionKey;
  /**
   * Visible while the RBAC matrix is still hydrating. Default is fail-CLOSED
   * (hidden); set true only where the current UX deliberately shows the row
   * first and lets the page re-gate (the projects Settings panel).
   */
  unloadedVisible?: boolean;
  /**
   * A platform feature flag code (`HRMS_LEAVE_TEAM_PAGES`) that must also be on. An app publishes its
   * flags to `@clarix/rbac`; a code it has not published reads off, so the row fails closed.
   */
  flag?: string;
}

/* ─────────────────────────── Filters ─────────────────────────── */

/**
 * A header filter popover with one or more checkbox groups whose selection is
 * written to the URL as comma-joined params (Calendar type/status). Each group
 * either starts with every option selected (`defaultAll`) or with none.
 */
export interface FilterPopoverConfig {
  /** Accessible name for the trigger button. */
  ariaLabel: string;
  groups: {
    key: string;
    label: string;
    /** URL param the selection is written to (comma-joined ids). */
    param: string;
    /** When true, the group starts with every option selected. */
    defaultAll?: boolean;
    options: { id: string; label: string; tone?: BadgeTone }[];
  }[];
  /** URL params to preserve when rewriting the query (e.g. the current view). */
  preserveParams?: string[];
}

/* ─────────────────────────── Search ─────────────────────────── */

export interface SearchConfig {
  placeholder?: string;
}

/* ─────────────────────────── Rows / sections / panels ─────────────────────────── */

/** A coloured text pill rendered at a row's trailing edge (role, ARCHIVED, …). */
export interface RowPill {
  label: string;
  /** Literal token classes from a fixed set — provider-produced only. */
  className: string;
  /** Native tooltip, for detail the pill is too small to spell out. */
  title?: string;
}

export interface RowConfig {
  id: string;
  label: string;
  icon?: IconName;
  /** Dynamic (name + colour) icon for entity rows like projects. */
  dynamicIcon?: { name: string; color?: string };
  /** Show the pinned-to-sidebar marker. */
  pinned?: boolean;
  /** Active when the pathname prefix-matches this path, ignoring `to`'s query. */
  activePath?: string;
  /** Navigate target. `?query` participates in active-state. */
  to?: string;
  /** Search-param navigation: active when `param` === `value`. */
  toParam?: { pathname: string; param: string; value: string; isDefault?: boolean };
  /** Current URL params carried across the navigation (e.g. the calendar view). */
  preserveParams?: string[];
  activeVariant?: 'fill' | 'text';
  /** Emphasis style: 'cta' = indigo text call-to-action (e.g. Schedule Meeting). */
  emphasis?: 'cta';
  /** Second line beneath the label (e.g. a meeting time range). */
  subtitle?: string;
  /** Trailing chevron affordance (e.g. upcoming-meeting rows). */
  trailingChevron?: boolean;
  /** Fixed leading-icon tone (overrides the default muted / active-indigo). */
  iconTone?: BadgeTone;
  /** Extra pathname prefixes that also mark this row active. */
  matchPaths?: string[];
  /** Active ONLY on an exact pathname match. */
  exact?: boolean;
  /** Extra active-state constraints on URL params (Spark feed vs admin). */
  activeExtra?: { paramAbsent?: string[]; paramNot?: { key: string; value: string } };
  /**
   * Trailing "opens a page" affordance. `subtle` is the muted panel default;
   * `strong` is the high-contrast variant the Projects panel uses so the two
   * page links read as the panel's primary exits.
   */
  externalLinkIcon?: 'subtle' | 'strong';
  visibleWhen?: Predicate;
  badge?: BadgeConfig;
  /** Trailing pills (role badge, ARCHIVED, …) — provider-produced. */
  pills?: RowPill[];
  /** Leading dot tone. */
  dotTone?: BadgeTone;
  /** Leading dot with a raw runtime colour (hex from data) — beats `dotTone`. */
  dotColor?: string;
  /** Trailing muted code text (e.g. a spark code). */
  code?: string;
  /**
   * Extra text the panel's search box matches on, beyond `label` — e.g. a
   * master-config category's backend `code`, which is what an admin who knows
   * the system actually types.
   */
  searchTerms?: string[];
  /** Nested sub-rows (a project's section tabs). */
  expandable?: { rows: RowConfig[] };
  /** Never render as active regardless of the URL. */
  neverActive?: boolean;
  /**
   * Provider-resolved active state, which OVERRIDES url matching entirely.
   *
   * For rows whose selection rule the engine can't express: CRM's "longest
   * match wins" (only the deepest matching row lights up) and its master-config
   * categories, where the page falls back to the first category of the current
   * group when no `?category=` is present. A provider that owns such rows must
   * set this on ALL of them (true on one, false on the rest), or the engine's
   * url matching will light a second row alongside it.
   */
  forceActive?: boolean;
  /**
   * Boolean flags a section's `toggleFilter` can filter on. Provider-produced
   * (e.g. `mine: true` for projects where the user holds a role).
   */
  flags?: Record<string, boolean>;
}

/**
 * Rows produced dynamically — either from an inline API call (declarative
 * mapping) or from a NAMED data provider (a hook the gateway registers, for
 * irreducibly-complex data: RBAC joins, preference sorting, cross-field counts).
 * The config stays pure-JSON either way — a provider is referenced by name.
 */
export type RowsSource = { api: ApiRequest; map: RowMapping } | { provider: string };

/** A React node produced by a provider (loose, to avoid a React dep here). */
export type ProviderNode = unknown;

/**
 * What a named SECTIONS provider returns — for a panel whose groups themselves
 * are data, not just their rows (CRM's master-config domain groups, which come
 * from the backend catalogue and drop out when empty).
 */
export interface SidebarSectionsResult {
  /** Fully-resolved sections, each with inline `rows`. */
  sections: SectionConfig[];
  isLoading?: boolean;
  isError?: boolean;
  /** Copy shown when every section resolved away. */
  emptyText?: string;
  /** Loading affordance while the sections resolve. */
  loadingVariant?: 'spinner' | 'skeleton' | 'text';
}

/** What a named data provider returns to the engine (already-shaped rows). */
export interface SidebarProviderResult {
  rows: RowConfig[];
  isLoading?: boolean;
  isError?: boolean;
  /** Dynamic empty copy ("No matching projects" vs "No projects assigned"). */
  emptyText?: string;
  /** Persist a drag-reorder (ordered row ids) — enables a reorderable tree. */
  onReorder?: (orderedIds: string[]) => void;
  /** Wrap each rendered row (e.g. a right-click context menu). */
  wrapRow?: (rowId: string, children: ProviderNode) => ProviderNode;
  /** Extra control rendered in the section header (e.g. a filter popover). */
  headerControl?: ProviderNode;
  /** Extra content rendered after the section (e.g. an edit popover). */
  footer?: ProviderNode;
}

export interface SectionConfig {
  id: string;
  label?: string;
  /** Render as the drag-reorderable entity tree (provider-backed). */
  tree?: boolean;
  /** Chevron collapse with a row count. */
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Thin rule above the section. */
  divider?: boolean;
  /**
   * Extra breathing room above the section, for a panel that separates its
   * groups by whitespace rather than by a rule (the workspace Settings panel:
   * General / Access / Security / Master). Use `divider` when you want a line.
   */
  spaced?: boolean;
  /** Render the divider + label only once the section has ≥ 1 row. */
  hideHeaderUntilData?: boolean;
  /** Multi-group filter popover in the header, writing selection to the URL. */
  filterPopover?: FilterPopoverConfig;
  /** Loading affordance: 'spinner' (default), 'skeleton', or 'text'. */
  loadingVariant?: 'spinner' | 'skeleton' | 'text';
  /** Append " · {count} {suffix}" to the label (e.g. "Today · 3 upcoming"). */
  labelCountSuffix?: string;
  /** Compact checkbox pill in the header; filters rows by a `flags` key. */
  toggleFilter?: {
    label: string;
    ariaLabel?: string;
    storageKey: string;
    /** Key in each row's `flags` a row must set to survive the filter. */
    field: string;
    defaultOn?: boolean;
    /** Hide the toggle unless this predicate passes (e.g. can-view-all). */
    visibleWhen?: Predicate;
  };
  rows?: RowConfig[];
  rowsSource?: RowsSource;
  visibleWhen?: Predicate;
  /** Empty / error copy. `emptyText` from a provider wins over this. */
  emptyText?: string;
  /** Empty copy while the panel search box is non-empty. */
  searchEmptyText?: string;
  errorText?: string;
}

export interface PanelConfig {
  /** Matches the `panelId` of the rail item that opens this panel. */
  id: string;
  /**
   * Panel header text, for both the pinned and the hover panel. Set it when the
   * rail label is abbreviated to fit the 64px rail ("Review" → "Review
   * Timesheets") or is otherwise not what the panel should be called. Omit it
   * and the chrome falls back to the rail item's own label.
   */
  title?: string;
  /** Page-link rows rendered directly below the search box, above all sections. */
  headerRows?: RowConfig[];
  search?: SearchConfig;
  sections: SectionConfig[];
  /**
   * Extra sections produced by a named provider, appended after `sections`.
   * Use when the GROUPS themselves are data — CRM's master-config panel, whose
   * domain groups come from the backend catalogue and disappear when empty.
   */
  sectionsSource?: { provider: string };
  /**
   * The first VISIBLE `toParam` row in the panel is active when its param is
   * absent from the URL — the "first tab is the default tab" rule the Reports
   * and Settings pages implement. Off by default: a panel whose rows only
   * select a view (Calendar) must show nothing active until the user picks.
   */
  defaultActiveFirst?: boolean;
}

/**
 * The rail's bottom utility strip, per app. Omit the whole object (or any
 * field) and each slot keeps its default — `appSwitcher` and `themeCustomizer`
 * render (opt-OUT), `trackerButton` does not (opt-IN, see below).
 *
 * `userMenu` is deliberately absent and NOT configurable: it is the only route
 * to Profile and Sign Out. An app that could hide it would strand a user inside
 * itself with no way out, which is also why the rail keeps it interactive
 * during a backend outage.
 */
export interface RailUtilitySlots {
  /** The radial app-switcher launcher. Hide it in a single-app deployment. */
  appSwitcher?: boolean;
  /**
   * The desktop Tracker download button. This one is opt-IN, not opt-out: it
   * only makes sense for an app whose users run the time tracker, so an app
   * that says nothing gets no button.
   */
  trackerButton?: boolean;
  /** "Customize navigation" (the rail theme editor) in the More popover. */
  themeCustomizer?: boolean;
}

export interface SidebarConfig {
  version: number;
  panels: PanelConfig[];
  /** Which of the rail's bottom utility slots this app shows. Default: all. */
  utility?: RailUtilitySlots;
}
